/**
 * Utilidad centralizada para clasificación de rechazo por porcentaje
 * Evita duplicación de lógica en múltiples componentes
 */

const BASE_REJECTION_CLASSIFICATION = [
  {
    min: -Infinity,
    max: 1,
    labelKey: "rejection_classification.excellent",
    rangeKey: "rejection_classification.range_excellent",
    statusIcon: "✓",
    colors: {
      bar: "bg-gradient-to-t from-green-400 to-green-500",
      text: "text-green-600",
      heatmap: "bg-green-100 text-green-700",
      badge: "bg-green-50 text-green-700 ring-green-600/20",
      circle: "bg-gradient-to-br from-green-400 to-green-500"
    }
  },
  {
    min: 1,
    max: 2,
    labelKey: "rejection_classification.good",
    rangeKey: "rejection_classification.range_good",
    statusIcon: "✓",
    colors: {
      bar: "bg-gradient-to-t from-blue-400 to-blue-500",
      text: "text-blue-600",
      heatmap: "bg-blue-100 text-blue-700",
      badge: "bg-blue-50 text-blue-700 ring-blue-600/20",
      circle: "bg-gradient-to-br from-blue-400 to-blue-500"
    }
  },
  {
    min: 2,
    max: 3,
    labelKey: "rejection_classification.acceptable",
    rangeKey: "rejection_classification.range_acceptable",
    statusIcon: "⚠",
    colors: {
      bar: "bg-gradient-to-t from-yellow-400 to-yellow-500",
      text: "text-yellow-600",
      heatmap: "bg-yellow-100 text-yellow-700",
      badge: "bg-yellow-50 text-yellow-700 ring-yellow-600/20",
      circle: "bg-gradient-to-br from-yellow-400 to-yellow-500"
    }
  },
  {
    min: 3,
    max: 5,
    labelKey: "rejection_classification.caution",
    rangeKey: "rejection_classification.range_caution",
    statusIcon: "⚠",
    colors: {
      bar: "bg-gradient-to-t from-orange-400 to-orange-500",
      text: "text-orange-600",
      heatmap: "bg-orange-100 text-orange-700",
      badge: "bg-orange-50 text-orange-700 ring-orange-600/20",
      circle: "bg-gradient-to-br from-orange-400 to-orange-500"
    }
  },
  {
    min: 5,
    max: Infinity,
    labelKey: "rejection_classification.critical",
    rangeKey: "rejection_classification.range_critical",
    statusIcon: "✗",
    colors: {
      bar: "bg-gradient-to-t from-red-400 to-red-500",
      text: "text-red-600",
      heatmap: "bg-red-100 text-red-700",
      badge: "bg-red-50 text-red-700 ring-red-600/20",
      circle: "bg-gradient-to-br from-red-400 to-red-500"
    }
  }
];

/**
 * Obtiene la clasificación con labels traducidos
 * @param {function} $t - Función de traducción de Vue i18n
 * @returns {array} Array de clasificaciones con labels traducidos
 */
export const getRejectionClassification = $t => {
  return BASE_REJECTION_CLASSIFICATION.map(classification => ({
    ...classification,
    label: $t(classification.labelKey),
    range: $t(classification.rangeKey)
  }));
};

// Mantener la compatibilidad con código existente que usa REJECTION_CLASSIFICATION
// Esta versión usa labels en español como fallback
export const REJECTION_CLASSIFICATION = BASE_REJECTION_CLASSIFICATION.map(
  c => ({
    ...c,
    label: {
      "rejection_classification.excellent": "Excelente",
      "rejection_classification.good": "Bueno",
      "rejection_classification.acceptable": "Aceptable",
      "rejection_classification.caution": "Precaución",
      "rejection_classification.critical": "Crítico"
    }[c.labelKey],
    range: {
      "rejection_classification.range_excellent": "0% - 1%",
      "rejection_classification.range_good": "1% - 2%",
      "rejection_classification.range_acceptable": "2% - 3%",
      "rejection_classification.range_caution": "3% - 5%",
      "rejection_classification.range_critical": "> 5%"
    }[c.rangeKey]
  })
);

/**
 * Obtiene la clasificación para un porcentaje de rechazo (con traducción)
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @param {array} classifications - Array de clasificaciones (puede incluir traducidas)
 * @returns {object} Objeto de clasificación con todos los datos
 */
export const getClassificationByPercentage = (
  rejectionPercentage,
  classifications = REJECTION_CLASSIFICATION
) => {
  const value = parseFloat(rejectionPercentage) || 0;
  return (
    classifications.find(c => value > c.min && value <= c.max) ||
    classifications[classifications.length - 1]
  );
};

/**
 * Obtiene las clases Tailwind para barras de visualización
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases de gradiente
 */
export const getRejectionBarClass = rejectionPercentage => {
  return getClassificationByPercentage(rejectionPercentage).colors.bar;
};

/**
 * Obtiene las clases Tailwind para texto de métricas
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases de texto coloreado
 */
export const getRejectionClass = rejectionPercentage => {
  return getClassificationByPercentage(rejectionPercentage).colors.text;
};

/**
 * Obtiene las clases Tailwind para tabla de calor (heatmap)
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases para celdas de tabla
 */
export const getHeatMapClasses = rejectionPercentage => {
  return (
    getClassificationByPercentage(rejectionPercentage).colors.heatmap +
    " font-medium"
  );
};

/**
 * Obtiene las clases Tailwind para badges de estado
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @returns {string} Clases completas para badge
 */
export const getStatusBadgeClass = rejectionPercentage => {
  const classes =
    getClassificationByPercentage(rejectionPercentage).colors.badge;
  return `inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium ring-1 ring-inset ${classes}`;
};

/**
 * Obtiene la etiqueta de estado con ícono
 * @param {number} rejectionPercentage - Porcentaje de rechazo
 * @param {array} classifications - Array de clasificaciones (opcional)
 * @returns {string} Etiqueta con ícono (ej: "✓ Excelente")
 */
export const getStatusLabel = (
  rejectionPercentage,
  classifications = REJECTION_CLASSIFICATION
) => {
  const classification = getClassificationByPercentage(
    rejectionPercentage,
    classifications
  );
  return `${classification.statusIcon} ${classification.label}`;
};

export default {
  REJECTION_CLASSIFICATION,
  getRejectionClassification,
  getClassificationByPercentage,
  getRejectionBarClass,
  getRejectionClass,
  getHeatMapClasses,
  getStatusBadgeClass,
  getStatusLabel
};
